import { Box, IconButton, Slider, Typography } from '@mui/material';
import VolumeUpRounded from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRounded from '@mui/icons-material/VolumeOffRounded';
import { useDispatch, useSelector } from 'react-redux';
import {
  MAX_VOLUME,
  MIN_VOLUME,
  VOLUME_CHANNELS,
  VOLUME_CHANNEL_LABELS,
  VolumeChannel,
} from './volumeSettings';
import { selectVolumeSettings, setChannelMuted, setChannelVolume } from './volumeSlice';
import { volumeControlsAvailable } from './volumeAvailability';

const sliderSx = {
  color: '#6F87F1',
  '& .MuiSlider-thumb': { width: 16, height: 16 },
  '& .MuiSlider-rail': { color: '#525969', opacity: 1 },
} as const;

type VolumeControlsProps = {
  /** A heading above the controls, for surfaces that do not label the section. */
  title?: string;
};

/**
 * One row per sound channel (piano, metronome, narration, sound effects): a
 * level slider and that channel's own mute. There is no mute over every
 * channel. Every surface that shows these renders this one component, so they
 * all edit the same settings.
 */
export default function VolumeControls({ title }: VolumeControlsProps) {
  const dispatch = useDispatch();
  const settings = useSelector(selectVolumeSettings);

  if (!volumeControlsAvailable()) return null;

  const onVolumeChange = (channel: VolumeChannel) => (_event: Event, value: number | number[]) =>
    dispatch(setChannelVolume({ channel, volume: Array.isArray(value) ? value[0] : value }));

  return (
    <Box
      data-testid="volume-controls"
      sx={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#ECECED' }}
    >
      {title && (
        <Typography variant="body1" sx={{ fontFamily: 'Lato', marginBottom: '4px' }}>
          {title}
        </Typography>
      )}
      {VOLUME_CHANNELS.map((channel) => {
        const { level, muted } = settings[channel];
        const label = VOLUME_CHANNEL_LABELS[channel];
        return (
          <Box
            key={channel}
            sx={{
              display: 'grid',
              gridTemplateColumns: '96px minmax(100px, 1fr) 36px 28px',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'Lato' }}>
              {label}
            </Typography>
            <Slider
              size="small"
              sx={{ ...sliderSx, opacity: muted ? 0.5 : 1 }}
              min={MIN_VOLUME}
              max={MAX_VOLUME}
              step={1}
              value={level}
              onChange={onVolumeChange(channel)}
              slotProps={{ input: { 'aria-label': `${label} volume` } }}
            />
            <Typography
              variant="body2"
              sx={{ fontFamily: 'Lato', textAlign: 'right', opacity: muted ? 0.5 : 1 }}
            >
              {level}%
            </Typography>
            <IconButton
              size="small"
              aria-label={`Mute ${label}`}
              aria-pressed={muted}
              onClick={() => dispatch(setChannelMuted({ channel, muted: !muted }))}
              sx={{ color: muted ? '#F2A0A0' : '#ECECED', padding: '2px' }}
            >
              {muted ? <VolumeOffRounded fontSize="small" /> : <VolumeUpRounded fontSize="small" />}
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}
